import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Input, Modal, notification } from 'antd';
import { Link } from 'react-router-dom';
import APIClient from '../api/api';
import { Button, Callout, StatusTag } from '../components/ui';
import { PageHead } from '../layout';
import {
  PartnerSetting,
  PartnerSettings,
  tierCopy,
  tierOrder,
  tierTone,
} from './PartnerAuctionSettings.model';

/** A pending edit, held as the text the operator typed. */
type Draft = Record<string, string>;

export default function PartnerAuctionSettings() {
  const [data, setData] = useState<PartnerSettings | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [typedConfirm, setTypedConfirm] = useState('');

  const load = useCallback(async () => {
    try {
      const next = await APIClient.getPartnerAuctionSettings();
      setData(next);
      setDraft({});
    } catch (e: any) {
      notification.error({ message: 'Could not load settings', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const settings = useMemo(() => data?.settings ?? [], [data]);

  const edited = useMemo(
    () => settings.filter((s) => draft[s.key] !== undefined && draft[s.key] !== s.display),
    [settings, draft],
  );

  /** A ceiling being raised widens the exposure, so it is confirmed by hand. */
  const raisedCeilings = useMemo(
    () =>
      edited.filter((s) => {
        if (!s.raiseIsRiskier) return false;
        const before = Number(s.display.replace(/[^0-9.]/g, ''));
        const after = Number((draft[s.key] ?? '').replace(/[^0-9.]/g, ''));
        return Number.isFinite(before) && Number.isFinite(after) && after > before;
      }),
    [edited, draft],
  );

  const economicEdited = edited.some((s) => s.tier === 'economic');
  const confirmPhrase = raisedCeilings.map((s) => draft[s.key]).join(' ');
  const confirmSatisfied = raisedCeilings.length === 0 || typedConfirm.trim() === confirmPhrase;

  const submit = async () => {
    setBusy(true);
    try {
      const values: Record<string, string> = {};
      edited.forEach((s) => {
        values[s.key] = draft[s.key];
      });
      const changed = await APIClient.updatePartnerAuctionSettings({ values, reason });
      notification.success({
        message: changed.length ? `Updated ${changed.length} setting${changed.length === 1 ? '' : 's'}` : 'Nothing changed',
      });
      setConfirming(false);
      setReason('');
      setTypedConfirm('');
      await load();
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.error ?? 'Could not save',
        description: e?.response?.data?.field ? `Field: ${e.response.data.field}` : undefined,
        duration: 8,
      });
    } finally {
      setBusy(false);
    }
  };

  const renderRow = (s: PartnerSetting) => {
    const value = draft[s.key] ?? s.display;
    const dirty = draft[s.key] !== undefined && draft[s.key] !== s.display;
    return (
      <div key={s.key} className="grid grid-cols-1 gap-1 border-b border-slate-100 py-3 md:grid-cols-[minmax(0,1fr)_200px]">
        <div className="pr-6">
          <div className="flex items-baseline gap-2">
            <span className="font-medium">{s.label}</span>
            <code className="text-[11px] text-slate-400">{s.key}</code>
            {dirty && <StatusTag tone="accent">changed</StatusTag>}
          </div>
          <p className="mt-0.5 text-[13px] leading-snug text-slate-500">{s.help}</p>
        </div>
        <div className="flex items-start gap-2">
          {s.kind === 'boolean' ? (
            <select
              className="h-8 w-full rounded border border-slate-300 px-2 text-sm"
              value={value}
              onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))}
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          ) : (
            <Input
              value={value}
              onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))}
              prefix={s.kind === 'money' ? <span className="text-slate-400">$</span> : undefined}
              className="tabular-nums"
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title="Partner Auction Settings"
        subtitle={
          <span>
            Economics, timing and ceilings. <Link to="/tools/partner-auctions">Back to candidates</Link>
          </span>
        }
        actions={
          <Button
            variant="primary"
            disabled={edited.length === 0 || busy}
            onClick={() => setConfirming(true)}
          >
            Review {edited.length > 0 ? `${edited.length} change${edited.length === 1 ? '' : 's'}` : 'changes'}
          </Button>
        }
      />

      {data && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
          <span className="text-slate-500">
            Config version <strong className="tabular-nums text-slate-900">{data.configVersion}</strong>
          </span>
          <span className="text-slate-500">
            Feed <code className="text-[12px]">{data.feedUrl || 'not configured'}</code>
            <span className="ml-1 text-[12px] text-slate-400">(config.json, read only)</span>
          </span>
        </div>
      )}

      {tierOrder.map((tier) => {
        const rows = settings.filter((s) => s.tier === tier);
        if (rows.length === 0) return null;
        const copy = tierCopy[tier];
        return (
          <section key={tier} className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="mb-1 flex items-baseline gap-2">
              <h2 className="text-sm font-semibold">{copy.title}</h2>
              <StatusTag tone={tierTone[tier]}>{tier}</StatusTag>
            </div>
            <p className="mb-2 max-w-3xl text-[13px] text-slate-500">{copy.blurb}</p>
            {rows.map(renderRow)}
          </section>
        );
      })}

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      <Modal
        open={confirming}
        title={`Apply ${edited.length} change${edited.length === 1 ? '' : 's'}`}
        okText="Apply"
        confirmLoading={busy}
        onCancel={() => setConfirming(false)}
        onOk={submit}
        okButtonProps={{ disabled: reason.trim().length === 0 || !confirmSatisfied }}
      >
        <ul className="mb-3 list-disc pl-5 text-sm">
          {edited.map((s) => (
            <li key={s.key}>
              <strong>{s.label}</strong>: {s.display} → {draft[s.key]}
            </li>
          ))}
        </ul>

        {economicEdited && (
          <Callout tone="warn">
            <strong>This changes how candidates are judged.</strong> Everything already discovered keeps the
            economics it was found under, so the two groups are not comparable in one total. The config version
            will be bumped so they stay separable.
          </Callout>
        )}

        {raisedCeilings.length > 0 && (
          <Callout tone="danger">
            <strong>You are raising a ceiling.</strong> This widens how much can be spent. Type the new{' '}
            {raisedCeilings.length === 1 ? 'value' : 'values'} to confirm:{' '}
            <code>{confirmPhrase}</code>
            <Input
              className="mt-2"
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              placeholder={confirmPhrase}
            />
          </Callout>
        )}

        <Form layout="vertical" className="mt-3">
          <Form.Item label="Reason (recorded against your account)" required>
            <Input.TextArea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>

      {data?.recent && data.recent.length > 0 && (
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold">Recent changes</h2>
          <ul className="flex flex-col gap-1.5 text-[13px]">
            {data.recent.map((e, i) => (
              <li key={i} className="flex gap-3 border-b border-slate-100 pb-1.5 last:border-0">
                <span className="w-40 shrink-0 tabular-nums text-slate-400">{e.at.replace('T', ' ').replace('Z', '')}</span>
                <span className="w-48 shrink-0 text-slate-600">{e.actor}</span>
                <span className="min-w-0 flex-1 break-all text-slate-500">{e.detail}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
